import { Link } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { categories, mainSiteUrl } from '@/mocks/categories';

const navIcons: Record<string, string> = {
  'Shoes': 'ri-footprint-line',
  'Hoodies/Sweaters': 'ri-shirt-line',
  'T-Shirts': 'ri-price-tag-3-line',
  'Jackets': 'ri-t-shirt-air-line',
  'Pants/Shorts': 'ri-archive-line',
  'Headwear': 'ri-user-star-line',
  'Sets': 'ri-stack-line',
  'Underwear/Underpants': 'ri-heart-line',
  'Jersey': 'ri-vip-line',
  'Accessories': 'ri-gemini-line',
  'Bags': 'ri-briefcase-line',
  'Belts': 'ri-link-m',
  'Perfumes': 'ri-drop-line',
  'Other Stuff': 'ri-more-line',
};

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-background-50/90 backdrop-blur-md">
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 flex-shrink-0">
            <div className="w-8 h-8 md:w-9 md:h-9 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-grid-line text-background-50 text-base md:text-lg"></i>
            </div>
            <span className="font-heading text-base md:text-lg font-semibold text-primary-700 whitespace-nowrap">
              OOPBuy Spreadsheet
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1 md:gap-3 flex-1 justify-center px-4">
            <Link to="/" className="px-3 py-2 rounded-full text-sm text-foreground-600 hover:text-foreground-900 hover:bg-background-100 transition-all whitespace-nowrap font-label">Startseite</Link>

            {/* Category Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-full text-sm text-foreground-600 hover:text-foreground-900 hover:bg-background-100 transition-all whitespace-nowrap font-label cursor-pointer"
              >
                <span>Kategorien</span>
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-0 bg-background-50 border border-foreground-200 rounded-xl shadow-sm py-2 w-60 max-h-96 overflow-y-auto z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-100 hover:text-foreground-900 transition-colors duration-150 cursor-pointer"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <div className="w-7 h-7 rounded-md overflow-hidden flex-shrink-0 bg-background-200">
                        <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                      </div>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link to="/artikel/oopbuy-spreadsheet-guide" className="px-3 py-2 rounded-full text-sm text-foreground-600 hover:text-foreground-900 hover:bg-background-100 transition-all whitespace-nowrap font-label">Anleitung</Link>
            <Link to="/einkaufsfuehrer" className="px-3 py-2 rounded-full text-sm text-foreground-600 hover:text-foreground-900 hover:bg-background-100 transition-all whitespace-nowrap font-label">Einkaufsführer</Link>
          </div>

          {/* Desktop CTA Buttons */}
          <div className="hidden md:flex items-center gap-2 flex-shrink-0">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-label font-medium hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              OOPBuy Spreadsheet
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 rounded-full border border-primary-500 text-primary-600 text-xs font-label font-medium hover:bg-primary-500 hover:text-background-50 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-foreground-200 bg-background-50 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-3 space-y-1">
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-background-100 font-label font-medium cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center">
                <i className="ri-home-4-line text-foreground-600"></i>
              </div>
              Startseite
            </Link>
            <Link
              to="/artikel/oopbuy-spreadsheet-guide"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-background-100 font-label font-medium cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center">
                <i className="ri-book-open-line text-foreground-600"></i>
              </div>
              Anleitung
            </Link>
            <Link
              to="/einkaufsfuehrer"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-background-100 font-label font-medium cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center">
                <i className="ri-shopping-bag-3-line text-foreground-600"></i>
              </div>
              Einkaufsführer
            </Link>

            {/* Mobile Categories */}
            <div className="border-t border-foreground-200 pt-3 mt-3">
              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-background-100 font-label font-medium cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
                    <i className="ri-apps-2-line text-accent-700"></i>
                  </div>
                  Kategorien
                </div>
                <i className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileCatOpen && (
                <div className="mt-1 space-y-1 pl-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-foreground-600 hover:bg-accent-100 hover:text-foreground-800 transition-colors cursor-pointer font-label"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <div className="w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                        <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                      </div>
                      <span>{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2 pt-3 border-t border-foreground-200">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="w-full px-5 py-3 rounded-full bg-primary-500 text-background-50 text-sm text-center font-label font-medium hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
              >
                OOPBuy Spreadsheet
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="w-full px-5 py-3 rounded-full border border-primary-500 text-primary-600 text-sm text-center font-label font-medium hover:bg-primary-500 hover:text-background-50 transition-all cursor-pointer whitespace-nowrap"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}